import { useHideMobileChrome, useMobilePreviewMode } from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import {
  MAP_CONFIG_MOBILE_OVERLAY_CLEARANCE,
  MOBILE_MINIMIZED_BAR_HEIGHT,
  MOBILE_OVERLAY_OFFSET_ABOVE_MINIMIZED,
  MOBILE_OVERLAY_OFFSET_ABOVE_PREVIEW,
  PREVIEW_PANEL_HEIGHT,
} from '@/constants/ui';

/** Mobile preview state + floating-overlay visibility for the configurator. */
export interface UseMapConfigOverlays {
  /** Whether the panel is in partial preview (store-backed, mobile only). */
  previewMode: boolean;
  /** Toggle preview mode (wired to the SlidePanel swipe gestures). */
  setPreviewMode: (isPreview: boolean) => void;
  /** Whether the floating overlays (search + settings bar) should render. */
  showOverlays: boolean;
  /**
   * CSS `bottom` for the map-area overlays (zoom pill + drag hint) so they
   * sit above the mobile panel in preview / minimized states; `undefined` on
   * desktop and when fully expanded (overlays keep their CSS default).
   */
  bottomOverlayOffset: string | undefined;
  /**
   * Numeric MapLibre camera bottom inset (px) for the same panel states, so the
   * center pin is framed above the mobile bottom sheet + its stacked overlays;
   * `0` on desktop and when fully expanded. Consumed by
   * {@link useMapConfigCameraPadding}.
   */
  mapBottomInset: number;
}

/**
 * Owns the configurator's mobile panel behaviour, mirroring the main map's
 * artifact detail panels: the panel starts in partial preview on mobile, and
 * the floating overlays hide once it is fully expanded (via the shared
 * `useHideMobileChrome` guard). `unregisterSlidePanel` resets
 * `previewMode` on close, so no cleanup is needed here.
 */
export function useMapConfigOverlays(isMobile: boolean): UseMapConfigOverlays {
  const { previewMode, setPreviewMode } = useMobilePreviewMode(isMobile);
  const isMinimized = useGeoMarkupStore(
    (state) => state.ui.slidePanel.isMinimized
  );
  const hideMobileChrome = useHideMobileChrome();

  // One branch over the mobile panel state drives both the overlay `bottom`
  // (CSS string for the DOM) and the camera bottom inset (px for MapLibre), so
  // the "occupied bottom region" stays a single source of truth.
  let bottomOverlayOffset: string | undefined;
  let mapBottomInset = 0;
  if (isMobile) {
    if (isMinimized) {
      bottomOverlayOffset = MOBILE_OVERLAY_OFFSET_ABOVE_MINIMIZED;
      mapBottomInset =
        MOBILE_MINIMIZED_BAR_HEIGHT + MAP_CONFIG_MOBILE_OVERLAY_CLEARANCE;
    } else if (previewMode) {
      bottomOverlayOffset = MOBILE_OVERLAY_OFFSET_ABOVE_PREVIEW;
      mapBottomInset =
        PREVIEW_PANEL_HEIGHT + MAP_CONFIG_MOBILE_OVERLAY_CLEARANCE;
    }
  }

  return {
    previewMode,
    setPreviewMode,
    showOverlays: !hideMobileChrome,
    bottomOverlayOffset,
    mapBottomInset,
  };
}
